import * as React from "react";
import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Play, Pause } from 'lucide-react';

interface OptimizedVideoProps {
  src: string;
  poster?: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  preload?: 'auto' | 'metadata' | 'none';
  'aria-label'?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  reducedMotion?: boolean;
}

export const OptimizedVideo = ({
  src,
  poster,
  className,
  autoPlay = false,
  muted = true,
  loop = false,
  controls = false,
  playsInline = true,
  preload = 'metadata',
  'aria-label': ariaLabel,
  width,
  height,
  priority = false,
  reducedMotion = false,
  ...props
}: OptimizedVideoProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [hasError, setHasError] = useState(false);
  const [inView, setInView] = useState(priority);
  const videoRef = useRef<HTMLVideoElement>(null);
  const webmSrc = src.endsWith('.mp4') ? src.replace(/\.mp4$/, '.webm') : undefined;
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(reducedMotion);

  // Check for user's motion preferences
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches || reducedMotion);
    
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [reducedMotion]);

  // Intersection Observer for performance
  useEffect(() => {
    if (priority || !videoRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: priority ? '50px' : '200px',
        threshold: 0.01
      }
    );

    observer.observe(videoRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const handleLoadedData = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    // Ignore errors fired before any source is attached (element still idle),
    // and errors bubbling up from an individual <source> that failed while a
    // later source may still succeed. Only a real element-level failure
    // (no usable source at all) counts.
    if (!inView) return;
    const el = videoRef.current;
    if (!el) return;
    if (!el.error && el.networkState !== el.NETWORK_NO_SOURCE) return;
    setHasError(true);
    console.warn('Video failed to load:', src);
  };

  // An autoplay rejection (NotAllowedError / AbortError) is NOT a load failure —
  // never surface it as a broken video.
  const handlePlayRejection = () => {
    setIsPlaying(false);
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(handlePlayRejection);
      }
    }
  };


  // Auto-play only when in view and muted (for policy compliance)
  useEffect(() => {
    if (inView && autoPlay && muted && videoRef.current && !hasError && !prefersReducedMotion) {
      // Use requestIdleCallback for non-priority videos to avoid blocking
      if (priority) {
        videoRef.current.play().catch(handlePlayRejection);
      } else {
        const playVideo = () => {
          if (videoRef.current) {
            videoRef.current.play().catch(handlePlayRejection);
          }
        };
        
        if ('requestIdleCallback' in window) {
          requestIdleCallback(playVideo, { timeout: 1000 });
        } else {
          setTimeout(playVideo, 100);
        }
      }
    }
  }, [inView, autoPlay, muted, hasError, priority, prefersReducedMotion]);

  // Graceful degradation: if the video genuinely cannot load, fall back to the
  // poster image (or nothing) — never render a broken-media placeholder.
  if (hasError) {
    if (!poster) return null;
    return (
      <div className={cn('relative overflow-hidden', className)} {...props}>
        <img
          src={poster}
          alt={ariaLabel || 'Video poster'}
          className="w-full h-full object-cover"
          loading={priority ? 'eager' : 'lazy'}
        />
      </div>
    );
  }


  return (
    <div className={cn('relative', className)} {...props}>
      {/* Loading placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
          {poster && (
            <img 
              src={poster} 
              alt={ariaLabel || 'Video poster'} 
              className="w-full h-full object-cover"
              loading={priority ? 'eager' : 'lazy'}
            />
          )}
        </div>
      )}

      {/* Video element */}
      <video
        ref={videoRef}
        key={inView ? 'live' : 'idle'}
        poster={poster}
        width={width}
        height={height}
        autoPlay={autoPlay && muted} // Only autoplay if muted for policy compliance
        muted={muted}
        loop={loop}
        controls={controls}
        playsInline={playsInline}
        preload={inView ? preload : 'none'}
        aria-label={ariaLabel}
        onLoadedData={handleLoadedData}
        onError={handleError}
        onPlay={handlePlay}
        onPause={handlePause}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
        {...props}
      >
        {/* Sources are only attached once the video is in view, so nothing is
            fetched early. A WebM sibling (when present) covers browsers that
            cannot decode H.264. */}
        {inView && <source src={src} type="video/mp4" />}
        {inView && webmSrc && <source src={webmSrc} type="video/webm" />}
        Your browser does not support the video tag.
      </video>


      {/* Custom play/pause overlay (only if no native controls) */}
      {!controls && !hasError && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors duration-300 group"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          <div className={cn(
            'p-4 rounded-full bg-black/30 backdrop-blur-sm transition-all duration-300',
            'group-hover:bg-black/50 group-hover:scale-110',
            isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-80'
          )}>
            {isPlaying ? (
              <Pause className="w-6 h-6 text-white" />
            ) : (
              <Play className="w-6 h-6 text-white ml-1" />
            )}
          </div>
        </button>
      )}
    </div>
  );
};

export default OptimizedVideo;